import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/Sheet";
import { Card, CardContent } from "@/components/ui/Card";
import { debounce } from "lodash";
import { useGetCountriesQuery, useLazyGetUserByUsernameQuery } from "@/services/UserApi";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { UserData, Gender } from "@/app/shared_enums/enums";
import { useTicket } from "@/context/TicketContext";
import { RegistrationFormData } from "@/app/types/user";
import { PhoneNumberUtil } from "google-libphonenumber";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/Form";
import PhoneInputField from "@/components/custom-component/PhoneInputField";
import { toast } from "@/components/ui/UseToast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { cn } from "@/lib/utils";
import { CountryTypes } from "@/app/types/shared_types";
import { Check, ChevronDownIcon, Search } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/Command";
import { FloatingLabelInput } from "@/components/ui/floating-lable/Floating";

interface SideSheetProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserSidePanel = ({ isOpen, setOpen }: SideSheetProps) => {
  const [isOrgEmail, setIsOrgEmail] = useState(false);
  const [country, setCountry] = useState(false);
  const { data: countries } = useGetCountriesQuery();

  const {currentTicket}=useTicket();
  const { addUser, loading ,addDetails} = useTicket();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<RegistrationFormData[]>(
    []
  );
  const [userId, setUserId] = useState<number | null>(null);
  const [getUserByUsername] = useLazyGetUserByUsernameQuery();
  const phoneUtil = PhoneNumberUtil.getInstance();

  const methods = useForm<UserData>();
  const {
    handleSubmit,
    setValue,
    reset,
    control,
    register,
    formState: { errors },
  } = methods;


    // useEffect(() => {
    //   if (
    //     currentTicket?.user_details &&
    //     currentTicket.user_details.username !== "org_email"
    //   ) {
    //     Object.entries(currentTicket.user_details).forEach(([key, value]) => {
    //       setValue(key as keyof UserData, value || "");
    //       setUserId(1)
    //     });
    //     setValue("title", currentTicket.title.trim());
    //     setValue("description", currentTicket?.description?.trim() || "");
    //   }
    // }, [currentTicket,isOpen, setValue]);

      useEffect(() => {
        if (currentTicket?.user_details) {
          if (currentTicket.user_details.username === "org_email") {
            setIsOrgEmail(true);
            // Reset form when org_email is detected to ensure all fields are empty
            reset();
            setValue("username", "org_email");
          } else {
            setIsOrgEmail(false);
            Object.entries(currentTicket.user_details).forEach(
              ([key, value]) => {
                setValue(key as keyof UserData, value || "");
                setUserId(1);
              }
            );
          }
          setValue("title", currentTicket.title.trim());
          setValue("description", currentTicket?.description?.trim() || "");
        }
      }, [currentTicket, isOpen, setValue, reset]);

  const handleSearch = debounce(async (term: string) => {
    if (isOrgEmail) return; // Disable search when in org_email mode
    const trimmedTerm = term.trim(); // Trim whitespace
    if (!trimmedTerm) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await getUserByUsername({ username: term }).unwrap();
      setSearchResults(response || []);
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  }, 1500);

  const handleSelectUser = (user: UserData) => {
    if (isOrgEmail) return;
    Object.entries(user).forEach(([key, value]) => {
      setValue(key as keyof UserData, value || "");
      setUserId(1);
    });
    setSearchResults([]);
  };


  const onSubmit = async (data: UserData) => {
    const isValid = await methods.trigger(); // Validate all fields before submission

    if (!isValid) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill all required fields correctly.",
      });
      return; // Stop execution if validation fails
    }

    try {
      const resp = await addUser(data);
      const resp_two= await addDetails({
        title: data.title ?? "",
        description: data.description,
      });

      toast({
        variant: resp.success ? "success" : "destructive",
        title: resp.message,
      });

      if (resp.success) {
        handleClose();
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: "Something went wrong. Please try again.",
      });
    }
  };


  const handleClose = () => {
    setOpen(false);
    reset({
      title: "",
      description: "",
    })
    setUserId(null);
  }
const validatePhone = (value: string | undefined | null) => {
  value = "+" + value;
  if (!value) {
    return true; // If value is empty, validation passes (based on requirements)
  }

  if (value.length > 20) {
    return "Phone number cannot exceed 20 digits";
  }

  if (value.length <= 4) {
    return true; // Pass validation if length is 5 or fewer
  }

  try {
    // Parse the phone number with the default country code (can be adjusted)
    const parsedNumber = phoneUtil.parseAndKeepRawInput(value);

    // Check if the parsed number is a valid phone number
    if (!phoneUtil.isValidNumber(parsedNumber)) {
      return "Invalid phone number";
    }

    return true; // Return true if validation passes
  } catch (error) {
    // Catch parsing errors and return an appropriate message
    return "Invalid phone number format";
  }
};

  return (
    <FormProvider {...methods}>
      <Sheet
        open={isOpen}
        onOpenChange={() => {
          reset();
          setOpen(false);
          setSearchTerm("");
          reset({
            title: "",
            description: "",
          });
        }}
      >
        <SheetContent hideCloseButton className="!max-w-[500px] py-0 p-4">
          <form onSubmit={handleSubmit(onSubmit)}>
            <SheetHeader>
              <div className="flex gap-2 w-full justify-end items-end">
                <Button
                  variant="outline"
                  onClick={handleClose} // Close sheet when clicked
                  className="flex items-center space-x-2 max-w-24"
                >
                  <ChevronDownIcon className="h-4 w-4" />
                  <span>Cancel</span>
                </Button>
                <Button
                  className="w-full sm:w-auto max-w-24"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </Button>
              </div>
            </SheetHeader>
            <h2 className="text-xl font-semibold mb-2">
              Search User or Fill Details
            </h2>
            <div className="pb-2">
              <div className="flex items-center relative w-auto">
                {/* search icon */}
                <i className="fa-solid fa-magnifying-glass size-4 text-gray-400 absolute left-1 z-10 ml-2"></i>
                <FloatingLabelInput
                  placeholder={"Search by ITS"}
                  icon={<Search />}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value.trim());
                    handleSearch(e.target.value.trim());
                  }}
                  id="specificInput"
                  className="specific w-52 lg:w-64 pl-8 text-sm placeholder:text-sm text-gray-400 h-8"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="mb-4 border rounded p-2 max-h-40 overflow-y-auto fancy-scroll absolute bg-white z-10 max-w-[22.5rem]">
                  {searchResults.map((user) => (
                    <div
                      key={user.username}
                      className="p-2 border-b last:border-none cursor-pointer hover:bg-gray-100"
                      onClick={() =>
                        handleSelectUser({
                          ...user,
                          phone_num: user.phone_num || "",
                        })
                      }
                    >
                      {user.username} ({user.email})
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Card className="py-4 justify-start p-0  items-start overflow-auto max-h-[75vh] custom-scrollbar !shadow-none border-0">
              <CardContent className="p-2">
                <div className="mb-2">
                  <label className="block text-sm font-medium">ITS</label>
                  <Input
                    type="text"
                    className="w-full"
                    {...register("username", {
                      required: {
                        value: true,
                        message: "Required field"
                      },
                      validate: (value) => value !== "orgemail" || "Add customer details ",
                      maxLength: {
                        value: 40,
                        message: "Username cannot exceed 40 characters",
                      },
                    })}
                    disabled={userId === 1}
                  />
                  {errors.username && (
                    <p className="text-sm text-red-500">
                      {errors.username.message}
                    </p>
                  )}
                </div>
                <div className=" mt-2">
                  <label className="block text-sm font-medium">
                    First Name
                  </label>
                  <Input
                    type="text"
                    className="w-full"
                    {...register("first_name", {
                      required: "Required",
                      pattern: {
                        value: /^[A-Za-z0-9 ]+$/,
                        message: "Only letters and numbers are allowed",
                      },
                      maxLength: {
                        value: 40,
                        message: "First name cannot exceed 40 characters",
                      },
                    })}
                  />
                  {errors.first_name && (
                    <p className="text-sm text-red-500">
                      {errors.first_name.message}
                    </p>
                  )}
                </div>
                <div className="mt-2">
                  <label className="block text-sm font-medium">Last Name</label>
                  <Input
                    type="text"
                    className="w-full"
                    {...register("last_name", {
                      required: "Required",
                      pattern: {
                        value: /^[A-Za-z0-9 ]+$/,
                        message: "Only letters and numbers are allowed",
                      },
                      maxLength: {
                        value: 40,
                        message: "Last name cannot exceed 40 characters",
                      },
                    })}
                  />
                  {errors.last_name && (
                    <p className="text-sm text-red-500">
                      {errors.last_name.message}
                    </p>
                  )}
                </div>
                <div className="mt-2">
                  <label className="block text-sm font-medium">Email</label>
                  <Input
                    type="email"
                    className="w-full"
                    {...register("email", {
                      required: "Required",
                      pattern: {
                        value:
                          /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                        message: "Incorrect email format",
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div className="mt-2">
                  <label className="block text-sm font-medium">Title</label>
                  <Input
                    className="w-full"
                    {...register("title", {
                      required: "Required",
                      maxLength: {
                        value: 40,
                        message: "Title cannot exceed 40 characters",
                      },
                    })}
                  />
                  {errors.title && (
                    <p className="text-sm text-red-500">
                      {errors.title.message}
                    </p>
                  )}
                </div>
                <div className="mt-2">
                  <label className="block text-sm font-medium">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    className="w-full border rounded p-2"
                    {...register("description", {
                      maxLength: {
                        value: 350,
                        message: "Description cannot exceed 350 characters",
                      },
                    })}
                  />
                  {errors.description && (
                    <p className="text-sm text-red-500">
                      {errors.description.message}
                    </p>
                  )}
                </div>
                <div className="relative ">
                  <FormLabel className=" text-black">Phone Number</FormLabel>
                  <Controller
                    name="phone_num"
                    control={control}
                    rules={{ required: "Required", validate: validatePhone }}
                    render={({
                      field: { onChange, value, onBlur },
                      fieldState: { error },
                    }) => (
                      <PhoneInputField
                        value={value as string}
                        onChange={onChange}
                        inputcolor="#000"
                        error={error?.message}
                        placeholder="Enter phone number"
                      />
                    )}
                  />
                </div>
                <div className="mb-2 mt-2">
                  <label className="block text-sm font-medium">Gender</label>
                  <select
                    {...register("gender", {
                      required: "Required",
                    })}
                    className="border rounded w-full p-2"
                  >
                    {Object.values(Gender).map((gender) => (
                      <option key={gender as Gender} value={gender as string}>
                        {gender as string}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="relative mt-2">
                  <FormField
                    control={methods.control}
                    name="country"
                    rules={{ required: "Required" }}
                    render={({ field, fieldState: { error } }) => (
                      <FormItem className="flex flex-col w-full">
                        <FormLabel className="text-black">
                          Country Name
                        </FormLabel>
                        <div className="flex flex-col w-full">
                          <Popover
                            open={country}
                            onOpenChange={setCountry}
                            modal={true}
                          >
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  className={cn(
                                    "font-normal text-black border-black border-[1px] justify-between hover:bg-transparent",
                                    !field.value && "  focus:border-black "
                                  )}
                                >
                                  {field.value
                                    ? countries?.find(
                                        (country: CountryTypes) =>
                                          country.id === field.value // Compare with numeric value
                                      )?.country // Display country name if selected
                                    : "Select country"}
                                  <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="p-0">
                              <Command>
                                <CommandList>
                                  <CommandInput placeholder="Select Country" />
                                  <CommandEmpty>No country found.</CommandEmpty>
                                  <CommandGroup>
                                    {countries &&
                                      countries.map((country: CountryTypes) => (
                                        <CommandItem
                                          value={country.country}
                                          key={country.id}
                                          onSelect={() => {
                                            methods.setValue(
                                              "country",
                                              country.id // Set country_id to country.id as number
                                            );
                                            setCountry(false);
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                              country.id === field.value
                                                ? "opacity-100"
                                                : "opacity-0"
                                            )}
                                          />
                                          {country.country}{" "}
                                        </CommandItem>
                                      ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <FormMessage>{error?.message}</FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </form>
        </SheetContent>
      </Sheet>
    </FormProvider>
  );
};

export default UserSidePanel;